import { Component, computed, effect, inject, input, untracked } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_STACKLAYOUT } from '@progress/kendo-angular-layout';
import { AbstractManageSurveyComponent } from '@unity/abstract/abstract-manage-survey.component';
import { ClientDetailedDto, ClientsClient, ClientUpdateDto, FormDataDto, TransitionMiniDto, TransitionRequestDto } from '@unity/clients';
import { AuthorizeDirective } from '@unity/directives/authorize.directive';
import { PageContainerComponent } from '@unity/layouts/page-container/page-container.component';
import { SurveyDataService } from '@unity/services/survey-data.service';
import { CardClientAgreementDurationSummarySharedComponent } from '@unity/shared/card-client-agreement-duration-summary-shared/card-client-agreement-duration-summary-shared.component';
import { CardClientBudgetSummarySharedComponent } from '@unity/shared/card-client-budget-summary-shared/card-client-budget-summary-shared.component';
import { ActionDeleteSharedComponent } from '@unity/shared/composed/action-delete-shared/action-delete-shared.component';
import { ActionSharedComponent } from '@unity/shared/composed/action-shared/action-shared.component';
import { CardManageSurveySharedComponent } from '@unity/shared/composed/card-manage-survey-shared/card-manage-survey-shared.component';
import { GridClientExpenseSharedComponent } from '@unity/shared/grid-client-expense-shared/grid-client-expense-shared.component';
import { WorkflowSelectorSharedComponent } from '@unity/shared/workflow-selector-shared/workflow-selector-shared.component';
import { QuickActions } from '@unity/utils/quick-actions';
import { UnityEntitlementPath } from '@unity/utils/types';
import { EMPTY } from 'rxjs';

@Component({
  selector: 'unity-manage-client-page',
  imports: [
    KENDO_STACKLAYOUT,
    KENDO_BUTTON,
    PageContainerComponent,
    ActionDeleteSharedComponent,
    WorkflowSelectorSharedComponent,
    ActionSharedComponent,
    AuthorizeDirective,
    CardClientAgreementDurationSummarySharedComponent,
    CardClientBudgetSummarySharedComponent,
    CardManageSurveySharedComponent,
    GridClientExpenseSharedComponent,
  ],
  templateUrl: './manage-client-page.component.html',
  styleUrl: './manage-client-page.component.scss',
})
export class ManageClientPageComponent extends AbstractManageSurveyComponent<ClientDetailedDto> {
  private readonly clientsClient = inject(ClientsClient);
  private readonly surveyDataService = inject(SurveyDataService);

  /* Inputs from Route */
  public readonly $agreementId = input.required({ alias: 'agreementId', transform: (value: string) => parseInt(value) });
  public readonly $clientId = input.required({ alias: 'clientId', transform: (value: string) => parseInt(value) });

  protected readonly $returnToAgreementAction = computed(() => QuickActions.ReturnToAgreement(this.$agreementId()));

  /* Impl */
  protected override mainResource = rxResource({
    params: () => {
      const resourceId = this.$resourceId();
      const agreementId = this.$agreementId();
      return agreementId && resourceId ? { agreementId, resourceId } : undefined;
    },
    stream: ({ params: { agreementId, resourceId } }) => this.clientsClient.getOneForAgreementAsDetailed(agreementId, resourceId),
  });

  protected override readonly $formSchema = computed(() => {
    const resource = this.$resource();
    return resource?.form_schema;
  });

  protected override updateResourceFn$ = (resourceId: number, data: FormDataDto, transition?: TransitionMiniDto) => {
    const agreementId = untracked(this.$agreementId);
    const updateDto: ClientUpdateDto = {
      transition_id: transition?.id ?? null,
      ...data,
    };
    return this.clientsClient.updateOneForAgreement(agreementId, resourceId, updateDto);
  };

  protected override deleteResourceFn$ = (resourceId: number) => {
    const agreementId = this.$agreementId();
    if (!agreementId) {
      return EMPTY;
    }
    return this.clientsClient.deleteOneForAgreement(agreementId, resourceId);
  };

  protected override editEntitlement: UnityEntitlementPath = 'Client:Update';
  protected override transitionEntitlement: UnityEntitlementPath = 'Client:Transition';
  protected override deleteEntitlement: UnityEntitlementPath = 'Client:Delete';
  protected override resourceName = 'Client';
  protected override $resourceId = this.$clientId;

  protected override availableTransitionsFn$ = (_resourceId: number) => {
    const agreementId = this.$agreementId();
    return this.availableTransitionsClient.getAllForClientAsMini(agreementId, _resourceId);
  };

  protected override doTransitionFn$ = (_resourceId: number, transition: TransitionRequestDto) => {
    const agreementId = this.$agreementId();
    return this.transitionsClient.setForClient(agreementId, _resourceId, transition);
  };

  /* Computed */
  protected readonly $contentHeader = computed(() => {
    const client = this.mainResource.value();
    return `Client: ${client?.reference_number_display}`;
  });

  protected readonly $contentHeaderSecondary = computed(() => {
    const client = this.mainResource.value();
    return `Client Name: ${client?.first_name} ${client?.last_name}`;
  });

  protected readonly $contentHeaderTertiary = computed(() => {
    const client = this.mainResource.value();
    return `Agreement: ${client?.agreement.name}`;
  });

  protected readonly $createClientExpenseAction = computed(() => {
    const agreementId = this.$agreementId();
    return QuickActions.CreateClientExpense(agreementId);
  });

  /* Data */
  public constructor() {
    super();

    effect(() => {
      const agreementId = this.$agreementId();
      this.surveyDataService.setAgreementId(agreementId);
    });
  }
}
